// ================================================================
// PROPRIETARY & CONFIDENTIAL — Mt Forge Inc.
// Copyright (c) 2026 Mt Forge. All rights reserved.
// ================================================================
import React, { useState } from 'react';
import { useAuth } from '../AuthContext';

const STEPS = [
  {
    number: 1,
    title: 'Submit Your Idea',
    description: 'AI interview captures your invention — problem, market, features, and vision in a structured format.',
    icon: '\uD83D\uDCA1',
    color: '#6366f1',
  },
  {
    number: 2,
    title: 'We Evaluate',
    description: 'Team reviews feasibility, market potential, technical requirements, and competitive landscape.',
    icon: '\uD83D\uDD0D',
    color: '#f59e0b',
  },
  {
    number: 3,
    title: 'Equity Split Agreed',
    description: 'Typically 60\u201370% MT / 30\u201340% inventor. Negotiated based on complexity, market size, and your contribution.',
    icon: '\uD83E\uDD1D',
    color: '#10b981',
  },
  {
    number: 4,
    title: 'We Build & Launch',
    description: 'Full app built, deployed, and maintained. You focus on domain expertise and early users.',
    icon: '\uD83D\uDE80',
    color: '#8b5cf6',
  },
];

const KEY_TERMS = [
  { text: 'MT builds the entire app \u2014 design, development, deployment, and hosting', icon: '\u2699\uFE0F' },
  { text: 'MT handles all patent filings and legal documentation', icon: '\uD83D\uDCDC' },
  { text: 'MT provides ongoing maintenance, updates, and infrastructure', icon: '\uD83D\uDD27' },
  { text: 'Inventor provides domain expertise and initial user base', icon: '\uD83E\uDDE0' },
  { text: 'Revenue split matches the agreed equity split', icon: '\uD83D\uDCB0' },
  { text: 'Inventor can buy back equity at fair market value after 2 years', icon: '\uD83D\uDD13' },
  { text: 'MT retains platform IP \u2014 inventor retains domain IP', icon: '\uD83D\uDEE1\uFE0F' },
];

const INDUSTRIES = [
  'Agriculture', 'Automotive', 'Cannabis', 'Construction', 'Education',
  'Energy / Oil & Gas', 'Event Management', 'Finance / Insurance', 'Healthcare / Nursing',
  'Hospitality', 'Logistics / Trucking', 'Manufacturing', 'Mining',
  'Real Estate', 'Retail / Beauty', 'SaaS / Technology', 'Wellness / Fitness', 'Other',
];

const FUNDING_OPTIONS = [
  'Self-funded',
  'Seeking Investment',
  'No Funding',
  'Grant / Government',
];

const DEMO_DEALS = [
  {
    inventor: 'Marcus Chen',
    idea: 'AI-Powered Grain Quality Scanner',
    industry: 'Agriculture',
    split: '65 / 35',
    status: 'Building',
    startDate: '2026-01-15',
  },
  {
    inventor: 'Sarah Okafor',
    idea: 'MediTrack Pro \u2014 Patient Outcome Tracker',
    industry: 'Healthcare / Nursing',
    split: '60 / 40',
    status: 'Live',
    startDate: '2025-09-22',
  },
  {
    inventor: 'Derek Lawson',
    idea: 'FleetPulse \u2014 Real-Time Tire Pressure Monitor',
    industry: 'Logistics / Trucking',
    split: '70 / 30',
    status: 'Negotiating',
    startDate: '2026-02-03',
  },
  {
    inventor: 'Priya Mehta',
    idea: 'EventSync \u2014 Vendor Coordination Platform',
    industry: 'Event Management',
    split: '65 / 35',
    status: 'Evaluating',
    startDate: '2026-02-08',
  },
  {
    inventor: 'Tom Bridger',
    idea: 'WellnessLink \u2014 Practitioner Scheduling Suite',
    industry: 'Wellness / Fitness',
    split: '60 / 40',
    status: 'Applied',
    startDate: '2026-02-11',
  },
];

const STATUS_COLORS = {
  Applied: { bg: '#6366f120', text: '#6366f1' },
  Evaluating: { bg: '#f59e0b20', text: '#f59e0b' },
  Negotiating: { bg: '#f97316', text: '#fff' },
  Building: { bg: '#10b98120', text: '#10b981' },
  Live: { bg: '#10b981', text: '#fff' },
};

const SUCCESS_STORIES = [
  {
    name: 'Sarah Okafor',
    title: 'MediTrack Pro',
    industry: 'Healthcare',
    split: '60 / 40',
    quote: 'I had the clinical knowledge but zero coding ability. MT built MediTrack Pro from my napkin sketch into a platform used by 14 clinics. I kept 40% equity and never wrote a line of code.',
    revenue: '$12,400/mo',
    avatar: '\uD83D\uDC69\u200D\u2695\uFE0F',
  },
  {
    name: 'James Whitfield',
    title: 'RanchGuard',
    industry: 'Agriculture',
    split: '65 / 35',
    quote: 'As a rancher, I knew exactly what we needed but had no way to build it. MT turned my idea into a working app in 8 weeks. The patent alone made it worthwhile.',
    revenue: '$8,200/mo',
    avatar: '\uD83E\uDDD1\u200D\uD83C\uDF3E',
  },
  {
    name: 'Linda Park',
    title: 'BookSmart Events',
    industry: 'Event Management',
    split: '60 / 40',
    quote: 'I was quoted $180,000 by a dev shop. MT built it for equity instead. Six months later, I have a live product, recurring revenue, and I still own 40% of the company.',
    revenue: '$6,800/mo',
    avatar: '\uD83D\uDC69\u200D\uD83D\uDCBC',
  },
];

const INITIAL_FORM = {
  ideaTitle: '',
  industry: '',
  problemDescription: '',
  targetMarket: '',
  fundingStatus: '',
  equitySplit: 65,
};

export default function CoDevPage() {
  const { user } = useAuth();
  const [form, setForm] = useState({ ...INITIAL_FORM });
  const [submitted, setSubmitted] = useState(false);
  const [showPipeline, setShowPipeline] = useState(false);

  const isOwner = user?.role === 'owner' || user?.role === 'admin';

  const handleChange = (field, value) => {
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!form.ideaTitle || !form.industry || !form.problemDescription || !form.targetMarket || !form.fundingStatus) return;
    setSubmitted(true);
  };

  const handleReset = () => {
    setForm({ ...INITIAL_FORM });
    setSubmitted(false);
  };

  return (
    <div className="page-body" style={{ padding: 24, maxWidth: 1100, margin: '0 auto' }}>

      {/* ── Hero Header ── */}
      <div style={{
        background: 'linear-gradient(135deg, #6366f115 0%, #8b5cf615 50%, #a78bfa10 100%)',
        border: '1px solid var(--border)',
        borderRadius: 16,
        padding: '48px 40px',
        textAlign: 'center',
        marginBottom: 32,
        position: 'relative',
        overflow: 'hidden',
      }}>
        <div style={{
          position: 'absolute', top: -40, right: -40,
          width: 160, height: 160, borderRadius: '50%',
          background: '#6366f108', border: '1px solid #6366f115',
        }} />
        <div style={{
          position: 'absolute', bottom: -30, left: -30,
          width: 120, height: 120, borderRadius: '50%',
          background: '#8b5cf608', border: '1px solid #8b5cf615',
        }} />
        <div style={{ fontSize: 40, marginBottom: 12 }}>{'\uD83E\uDD1D'}</div>
        <h1 style={{
          color: 'var(--text)', fontSize: 32, fontWeight: 800,
          margin: '0 0 10px', letterSpacing: -0.5,
        }}>
          Co-Development Partnerships
        </h1>
        <p style={{
          color: 'var(--text-muted)', fontSize: 16, margin: 0,
          maxWidth: 640, marginLeft: 'auto', marginRight: 'auto', lineHeight: 1.6,
        }}>
          Can&apos;t afford to build your app? We&apos;ll build it for equity.
          You keep 30&ndash;40% ownership &mdash; we do all the work.
        </p>
      </div>

      {/* ── How It Works ── */}
      <div style={{
        background: 'var(--card)', borderRadius: 14, border: '1px solid var(--border)',
        padding: 28, marginBottom: 28,
      }}>
        <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700, margin: '0 0 24px' }}>
          How It Works
        </h2>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))',
          gap: 20, position: 'relative',
        }}>
          {STEPS.map((step, idx) => (
            <div key={step.number} style={{
              padding: 22, borderRadius: 12,
              background: `${step.color}08`,
              border: `1px solid ${step.color}25`,
              position: 'relative',
              textAlign: 'center',
            }}>
              <div style={{
                width: 52, height: 52, borderRadius: '50%',
                background: `${step.color}18`, border: `2px solid ${step.color}`,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                margin: '0 auto 14px', fontSize: 22,
              }}>
                {step.icon}
              </div>
              <div style={{
                position: 'absolute', top: 10, left: 14,
                fontSize: 11, fontWeight: 800, color: step.color,
                background: `${step.color}18`, borderRadius: 6, padding: '2px 8px',
              }}>
                Step {step.number}
              </div>
              <h3 style={{
                color: 'var(--text)', fontSize: 15, fontWeight: 700,
                margin: '0 0 8px',
              }}>
                {step.title}
              </h3>
              <p style={{
                color: 'var(--text-muted)', fontSize: 12, margin: 0,
                lineHeight: 1.55,
              }}>
                {step.description}
              </p>
              {idx < STEPS.length - 1 && (
                <div style={{
                  position: 'absolute', top: '50%', right: -14, transform: 'translateY(-50%)',
                  color: 'var(--border)', fontSize: 18, fontWeight: 700,
                  display: 'none',
                }}>
                  {'\u2192'}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* ── The Deal ── */}
      <div style={{
        background: 'var(--card)', borderRadius: 14, border: '1px solid var(--border)',
        padding: 28, marginBottom: 28,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 20 }}>
          <div style={{
            width: 36, height: 36, borderRadius: 8,
            background: '#6366f118', display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: 18,
          }}>
            {'\uD83D\uDCCB'}
          </div>
          <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700, margin: 0 }}>
            The Deal
          </h2>
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: 12,
        }}>
          {KEY_TERMS.map((term, idx) => (
            <div key={idx} style={{
              display: 'flex', alignItems: 'flex-start', gap: 12,
              padding: '14px 16px', borderRadius: 10,
              background: 'var(--bg)', border: '1px solid var(--border)',
            }}>
              <span style={{ fontSize: 18, flexShrink: 0, marginTop: 1 }}>{term.icon}</span>
              <span style={{ color: 'var(--text)', fontSize: 13, lineHeight: 1.5 }}>
                {term.text}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* ── Application Form ── */}
      <div style={{
        background: 'var(--card)', borderRadius: 14, border: '1px solid var(--border)',
        padding: 28, marginBottom: 28,
      }}>
        <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700, margin: '0 0 6px' }}>
          Submit Your Application
        </h2>
        <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: '0 0 24px' }}>
          Tell us about your idea. We review every submission within 48 hours.
        </p>

        {submitted ? (
          <div style={{
            textAlign: 'center', padding: '48px 24px',
            background: '#10b98110', borderRadius: 12, border: '1px solid #10b98130',
          }}>
            <div style={{ fontSize: 48, marginBottom: 16 }}>{'\u2705'}</div>
            <h3 style={{ color: 'var(--text)', fontSize: 20, fontWeight: 700, margin: '0 0 8px' }}>
              Application Submitted
            </h3>
            <p style={{ color: 'var(--text-muted)', fontSize: 14, margin: '0 0 20px' }}>
              Thank you for submitting &ldquo;{form.ideaTitle}&rdquo;. Our team will review your idea and get back to you within 48 hours.
            </p>
            <button
              onClick={handleReset}
              style={{
                padding: '10px 24px', borderRadius: 8, border: 'none',
                background: '#6366f1', color: '#fff', fontSize: 13,
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              Submit Another Idea
            </button>
          </div>
        ) : (
          <form onSubmit={handleSubmit}>
            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18,
              marginBottom: 18,
            }}>
              {/* Idea Title */}
              <div>
                <label style={{
                  display: 'block', color: 'var(--text)', fontSize: 12,
                  fontWeight: 600, marginBottom: 6,
                }}>
                  Idea Title <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <input
                  type="text"
                  value={form.ideaTitle}
                  onChange={(e) => handleChange('ideaTitle', e.target.value)}
                  placeholder="e.g., AI-Powered Grain Quality Scanner"
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 8,
                    border: '1px solid var(--border)', background: 'var(--bg)',
                    color: 'var(--text)', fontSize: 13, outline: 'none',
                    boxSizing: 'border-box',
                  }}
                />
              </div>

              {/* Industry */}
              <div>
                <label style={{
                  display: 'block', color: 'var(--text)', fontSize: 12,
                  fontWeight: 600, marginBottom: 6,
                }}>
                  Industry <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={form.industry}
                  onChange={(e) => handleChange('industry', e.target.value)}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 8,
                    border: '1px solid var(--border)', background: 'var(--bg)',
                    color: form.industry ? 'var(--text)' : 'var(--text-muted)',
                    fontSize: 13, outline: 'none', boxSizing: 'border-box',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">Select an industry...</option>
                  {INDUSTRIES.map(ind => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
            </div>

            {/* Problem Description */}
            <div style={{ marginBottom: 18 }}>
              <label style={{
                display: 'block', color: 'var(--text)', fontSize: 12,
                fontWeight: 600, marginBottom: 6,
              }}>
                Problem Description <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                value={form.problemDescription}
                onChange={(e) => handleChange('problemDescription', e.target.value)}
                placeholder="What problem does your idea solve? Who experiences this problem? How are they currently dealing with it?"
                rows={4}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 8,
                  border: '1px solid var(--border)', background: 'var(--bg)',
                  color: 'var(--text)', fontSize: 13, outline: 'none',
                  resize: 'vertical', fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            {/* Target Market */}
            <div style={{ marginBottom: 18 }}>
              <label style={{
                display: 'block', color: 'var(--text)', fontSize: 12,
                fontWeight: 600, marginBottom: 6,
              }}>
                Target Market <span style={{ color: '#ef4444' }}>*</span>
              </label>
              <textarea
                value={form.targetMarket}
                onChange={(e) => handleChange('targetMarket', e.target.value)}
                placeholder="Who would use this app? How many potential users/businesses exist? What would they pay?"
                rows={3}
                style={{
                  width: '100%', padding: '10px 14px', borderRadius: 8,
                  border: '1px solid var(--border)', background: 'var(--bg)',
                  color: 'var(--text)', fontSize: 13, outline: 'none',
                  resize: 'vertical', fontFamily: 'inherit',
                  boxSizing: 'border-box',
                }}
              />
            </div>

            <div style={{
              display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 18,
              marginBottom: 24,
            }}>
              {/* Funding Status */}
              <div>
                <label style={{
                  display: 'block', color: 'var(--text)', fontSize: 12,
                  fontWeight: 600, marginBottom: 6,
                }}>
                  Current Funding Status <span style={{ color: '#ef4444' }}>*</span>
                </label>
                <select
                  value={form.fundingStatus}
                  onChange={(e) => handleChange('fundingStatus', e.target.value)}
                  style={{
                    width: '100%', padding: '10px 14px', borderRadius: 8,
                    border: '1px solid var(--border)', background: 'var(--bg)',
                    color: form.fundingStatus ? 'var(--text)' : 'var(--text-muted)',
                    fontSize: 13, outline: 'none', boxSizing: 'border-box',
                    cursor: 'pointer',
                  }}
                >
                  <option value="">Select funding status...</option>
                  {FUNDING_OPTIONS.map(opt => (
                    <option key={opt} value={opt}>{opt}</option>
                  ))}
                </select>
              </div>

              {/* Equity Split Slider */}
              <div>
                <label style={{
                  display: 'block', color: 'var(--text)', fontSize: 12,
                  fontWeight: 600, marginBottom: 6,
                }}>
                  Preferred Equity Split (MT / Inventor)
                </label>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: 14,
                  padding: '8px 14px', borderRadius: 8,
                  background: 'var(--bg)', border: '1px solid var(--border)',
                }}>
                  <input
                    type="range"
                    min={50}
                    max={70}
                    step={5}
                    value={form.equitySplit}
                    onChange={(e) => handleChange('equitySplit', parseInt(e.target.value, 10))}
                    style={{
                      flex: 1, height: 4, cursor: 'pointer',
                      accentColor: '#6366f1',
                    }}
                  />
                  <div style={{
                    fontSize: 13, fontWeight: 700, color: '#6366f1',
                    whiteSpace: 'nowrap', minWidth: 70, textAlign: 'right',
                  }}>
                    {form.equitySplit} / {100 - form.equitySplit}
                  </div>
                </div>
                <div style={{
                  display: 'flex', justifyContent: 'space-between',
                  marginTop: 4, fontSize: 10, color: 'var(--text-muted)',
                }}>
                  <span>50 / 50</span>
                  <span>70 / 30</span>
                </div>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              style={{
                width: '100%', padding: '14px 24px', borderRadius: 10,
                border: 'none', background: '#6366f1', color: '#fff',
                fontSize: 15, fontWeight: 700, cursor: 'pointer',
                letterSpacing: 0.3,
                transition: 'opacity 0.2s',
                opacity: (!form.ideaTitle || !form.industry || !form.problemDescription || !form.targetMarket || !form.fundingStatus) ? 0.5 : 1,
              }}
              disabled={!form.ideaTitle || !form.industry || !form.problemDescription || !form.targetMarket || !form.fundingStatus}
            >
              Submit Application
            </button>
          </form>
        )}
      </div>

      {/* ── Active Deals Pipeline (Owner Only) ── */}
      {isOwner && (
        <div style={{
          background: 'var(--card)', borderRadius: 14, border: '1px solid var(--border)',
          padding: 28, marginBottom: 28,
        }}>
          <div style={{
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            marginBottom: 20,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
              <div style={{
                width: 36, height: 36, borderRadius: 8,
                background: '#8b5cf618', display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontSize: 18,
              }}>
                {'\uD83D\uDCCA'}
              </div>
              <div>
                <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700, margin: 0 }}>
                  Active Deals Pipeline
                </h2>
                <p style={{ color: 'var(--text-muted)', fontSize: 12, margin: 0 }}>
                  {DEMO_DEALS.length} co-development deals in progress
                </p>
              </div>
            </div>
            <button
              onClick={() => setShowPipeline(!showPipeline)}
              style={{
                padding: '8px 16px', borderRadius: 8, border: '1px solid var(--border)',
                background: 'var(--bg)', color: 'var(--text)', fontSize: 12,
                fontWeight: 600, cursor: 'pointer',
              }}
            >
              {showPipeline ? 'Collapse' : 'Expand'}
            </button>
          </div>

          {/* Status Summary Badges */}
          <div style={{ display: 'flex', gap: 10, marginBottom: showPipeline ? 20 : 0, flexWrap: 'wrap' }}>
            {Object.entries(
              DEMO_DEALS.reduce((acc, d) => { acc[d.status] = (acc[d.status] || 0) + 1; return acc; }, {})
            ).map(([status, count]) => {
              const sc = STATUS_COLORS[status] || { bg: '#6366f120', text: '#6366f1' };
              return (
                <div key={status} style={{
                  padding: '6px 14px', borderRadius: 20,
                  background: sc.bg, color: sc.text,
                  fontSize: 12, fontWeight: 600,
                  border: sc.text === '#fff' ? 'none' : `1px solid ${sc.text}25`,
                }}>
                  {status}: {count}
                </div>
              );
            })}
          </div>

          {showPipeline && (
            <div style={{ overflowX: 'auto' }}>
              <table style={{
                width: '100%', borderCollapse: 'collapse', fontSize: 13,
              }}>
                <thead>
                  <tr>
                    {['Inventor', 'Idea', 'Industry', 'Split (MT/Inv)', 'Status', 'Start Date', ''].map((h, i) => (
                      <th key={i} style={{
                        textAlign: 'left', padding: '10px 14px',
                        color: 'var(--text-muted)', fontSize: 11, fontWeight: 600,
                        textTransform: 'uppercase', letterSpacing: 0.5,
                        borderBottom: '1px solid var(--border)',
                        whiteSpace: 'nowrap',
                      }}>
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {DEMO_DEALS.map((deal, idx) => {
                    const sc = STATUS_COLORS[deal.status] || { bg: '#6366f120', text: '#6366f1' };
                    return (
                      <tr key={idx} style={{
                        borderBottom: idx < DEMO_DEALS.length - 1 ? '1px solid var(--border)' : 'none',
                      }}>
                        <td style={{ padding: '12px 14px', color: 'var(--text)', fontWeight: 600 }}>
                          {deal.inventor}
                        </td>
                        <td style={{ padding: '12px 14px', color: 'var(--text)', maxWidth: 260 }}>
                          {deal.idea}
                        </td>
                        <td style={{ padding: '12px 14px', color: 'var(--text-muted)' }}>
                          {deal.industry}
                        </td>
                        <td style={{ padding: '12px 14px', color: '#6366f1', fontWeight: 600 }}>
                          {deal.split}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <span style={{
                            display: 'inline-block', padding: '4px 12px', borderRadius: 20,
                            background: sc.bg, color: sc.text,
                            fontSize: 11, fontWeight: 600,
                            border: sc.text === '#fff' ? 'none' : `1px solid ${sc.text}25`,
                          }}>
                            {deal.status}
                          </span>
                        </td>
                        <td style={{ padding: '12px 14px', color: 'var(--text-muted)', fontSize: 12 }}>
                          {deal.startDate}
                        </td>
                        <td style={{ padding: '12px 14px' }}>
                          <button style={{
                            padding: '6px 14px', borderRadius: 6, border: '1px solid var(--border)',
                            background: 'var(--bg)', color: 'var(--text)', fontSize: 11,
                            fontWeight: 600, cursor: 'pointer', whiteSpace: 'nowrap',
                          }}>
                            View Details
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      {/* ── Success Stories ── */}
      <div style={{
        background: 'var(--card)', borderRadius: 14, border: '1px solid var(--border)',
        padding: 28,
      }}>
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <h2 style={{ color: 'var(--text)', fontSize: 18, fontWeight: 700, margin: '0 0 6px' }}>
            Success Stories
          </h2>
          <p style={{ color: 'var(--text-muted)', fontSize: 13, margin: 0 }}>
            Real co-development deals that went from idea to revenue
          </p>
        </div>
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
          gap: 18,
        }}>
          {SUCCESS_STORIES.map((story, idx) => (
            <div key={idx} style={{
              padding: 22, borderRadius: 12,
              background: 'var(--bg)', border: '1px solid var(--border)',
              display: 'flex', flexDirection: 'column', gap: 14,
            }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={{
                  width: 44, height: 44, borderRadius: '50%',
                  background: '#6366f115', border: '2px solid #6366f140',
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  fontSize: 22,
                }}>
                  {story.avatar}
                </div>
                <div>
                  <div style={{ color: 'var(--text)', fontSize: 14, fontWeight: 700 }}>
                    {story.name}
                  </div>
                  <div style={{ color: '#6366f1', fontSize: 12, fontWeight: 600 }}>
                    {story.title} &mdash; {story.industry}
                  </div>
                </div>
              </div>
              <p style={{
                color: 'var(--text-muted)', fontSize: 12, lineHeight: 1.6,
                margin: 0, fontStyle: 'italic', flex: 1,
              }}>
                &ldquo;{story.quote}&rdquo;
              </p>
              <div style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                paddingTop: 12, borderTop: '1px solid var(--border)',
              }}>
                <div>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
                    Equity Split
                  </div>
                  <div style={{ fontSize: 13, color: 'var(--text)', fontWeight: 700 }}>
                    {story.split}
                  </div>
                </div>
                <div style={{ textAlign: 'right' }}>
                  <div style={{ fontSize: 10, color: 'var(--text-muted)', textTransform: 'uppercase', fontWeight: 600 }}>
                    Monthly Revenue
                  </div>
                  <div style={{ fontSize: 13, color: '#10b981', fontWeight: 700 }}>
                    {story.revenue}
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

    </div>
  );
}
